'use client';

import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { Children, isValidElement, type ReactElement, type ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';
import { inputClass } from './input-class';

type OptionProps = { value?: string | number; disabled?: boolean; children?: ReactNode };
type Opt = { value: string; label: ReactNode; disabled?: boolean };
type Group = { label: string | null; options: Opt[] };

export interface SelectProps {
  value?: string;
  /** Same shape as a native select's change event, so callers read `e.target.value`. */
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** `<option>` and `<optgroup>` elements, as with a native select. */
  children: ReactNode;
}

/** Reads the native `<option>` / `<optgroup>` children into groups. */
function parse(children: ReactNode): Group[] {
  const groups: Group[] = [];
  const loose: Group = { label: null, options: [] };
  const opt = (el: ReactElement<OptionProps>): Opt => ({ value: String(el.props.value ?? ''), label: el.props.children, disabled: el.props.disabled });
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if (child.type === 'option') {
      if (!groups.includes(loose)) groups.push(loose);
      loose.options.push(opt(child as ReactElement<OptionProps>));
    } else if (child.type === 'optgroup') {
      const el = child as ReactElement<{ label?: string; children?: ReactNode }>;
      const options = Children.toArray(el.props.children).filter((c): c is ReactElement<OptionProps> => isValidElement(c) && c.type === 'option').map(opt);
      groups.push({ label: el.props.label ?? null, options });
    }
  }
  return groups;
}

/**
 * The platform's dropdown: same field look as inputs, list drawn in the app's colors
 * (the native select list is painted by the OS and ignores the theme).
 */
export function Select({ value = '', onChange, disabled, className, id, children, ...aria }: SelectProps) {
  const [open, setOpen] = useState(false);
  const groups = parse(children);
  const selected = groups.flatMap((g) => g.options).find((o) => o.value === value);

  return (
    <DropdownPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownPrimitive.Trigger
        id={id}
        disabled={disabled}
        aria-label={aria['aria-label']}
        className={cn(
          inputClass,
          'group flex cursor-pointer items-center justify-between gap-2 text-left disabled:cursor-not-allowed data-[state=open]:border-lime/60 data-[state=open]:ring-2 data-[state=open]:ring-lime/15',
          className,
        )}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-fg-disabled')}>{selected?.label ?? 'Selecione'}</span>
        <ChevronDown className="size-3.5 shrink-0 text-fg-3 transition-transform duration-150 group-data-[state=open]:rotate-180 group-data-[state=open]:text-lime" aria-hidden />
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-h-[min(340px,var(--radix-dropdown-menu-content-available-height))] min-w-[max(var(--radix-dropdown-menu-trigger-width),10rem)] overflow-y-auto rounded-[12px] border border-border bg-panel p-1 shadow-xl shadow-black/40 data-[state=open]:animate-fade-in"
        >
          <DropdownPrimitive.RadioGroup value={value} onValueChange={(v) => onChange?.({ target: { value: v } })}>
            {groups.map((g, i) => (
              <div key={g.label ?? `g${i}`}>
                {i > 0 && <DropdownPrimitive.Separator className="my-1 h-px bg-border-soft" />}
                {g.label && <DropdownPrimitive.Label className="px-2.5 pt-1.5 pb-1 text-[10.5px] font-medium tracking-wider text-fg-3 uppercase">{g.label}</DropdownPrimitive.Label>}
                {g.options.map((o) => (
                  <DropdownPrimitive.RadioItem
                    key={o.value}
                    value={o.value}
                    disabled={o.disabled}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-[13px] whitespace-nowrap text-fg-2 outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[highlighted]:bg-hover data-[highlighted]:text-fg data-[state=checked]:font-medium data-[state=checked]:text-lime"
                  >
                    <span>{o.label}</span>
                    <DropdownPrimitive.ItemIndicator>
                      <Check className="size-3.5 text-lime" aria-hidden />
                    </DropdownPrimitive.ItemIndicator>
                  </DropdownPrimitive.RadioItem>
                ))}
              </div>
            ))}
          </DropdownPrimitive.RadioGroup>
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}
